import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import {
  Box,
  Card,
  Grid,
  Stack,
  Button,
  Divider,
  Container,
  Typography,
  CircularProgress,
  Alert,
  TextField,
  MenuItem,
  Chip,
} from '@mui/material'

import { getUserIdFromToken } from 'src/utils/jwt'

import { PermissionStatus } from '../enum/PermissionStatus'
import { PermissionType } from '../enum/PermissionType'
import { statusTranslations, typeTranslations, getStatusColor } from '../translations'
import { 
  type Permission, 
  updatePermission,
  getPermissionById,
  updatePermissionStatus,
  type UpdateLeaveRequestDto
} from '../../../api/permissionApi'

// Enum örnekleri (kendi enumlarınızı kullanın)
const LeaveStatus = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
};
const LeaveType = {
  ANNUAL: 'ANNUAL',
  SICK: 'SICK',
  UNPAID: 'UNPAID',
};

export default function PermissionEdit() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [permission, setPermission] = useState<Permission | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<any>({})

  useEffect(() => {
    const loadPermission = async () => {
      try {
        setLoading(true)
        setError(null)
        
        if (!id) {
          throw new Error('İzin ID\'si bulunamadı')
        }

        const data = await getPermissionById(id)
        setPermission(data)
        // Form verilerini mevcut izin bilgileriyle doldur
        setFormData(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Bir hata oluştu')
      } finally {
        setLoading(false)
      }
    }

    loadPermission()
  }, [id])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: any) => ({
      ...prev,
      [event.target.name]: event.target.value
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      if (!id) {
        throw new Error('İzin ID\'si bulunamadı')
      }

      await updatePermission({
        ...formData,
        id
      })
      navigate('/dashboard/permissions')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Güncelleme sırasında bir hata oluştu')
    } finally {
      setSaving(false)
    }
  }

  const handleStatusChange = async (approved: boolean) => {
    try {
      const userId = getUserIdFromToken()
      if (!userId || !permission) {
        throw new Error('Kullanıcı ID\'si veya izin bilgisi bulunamadı')
      }

      await updatePermissionStatus(permission.id, {
        status: approved ? PermissionStatus.APPROVED : PermissionStatus.REJECTED,
        approvedBy: userId
      })

      navigate('/dashboard/permissions')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'İşlem sırasında bir hata oluştu')
    }
  }

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      </Container>
    )
  }

  if (!permission) {
    return (
      <Container>
        <Card>
          <Typography variant="h6" sx={{ p: 3 }}>
            İzin talebi bulunamadı
          </Typography>
        </Card>
      </Container>
    )
  }

  return (
    <Container>
        <Card sx={{ p: 3 }}>
          <Typography variant="h4" sx={{ mb: 3 }}>
            İzin Düzenle
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Typography variant="subtitle1">
                    <strong>Çalışan:</strong> {formData.employee ? `${formData.employee.firstName} ${formData.employee.lastName}` : '-'}
                  </Typography>
                  <TextField
                    fullWidth
                    type="date"
                    name="startDate"
                    label="Başlangıç Tarihi"
                    value={formData.startDate || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    fullWidth
                    type="date"
                    name="endDate"
                    label="Bitiş Tarihi"
                    value={formData.endDate || ''}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    select
                    fullWidth
                    name="leaveType"
                    label="İzin Türü"
                    value={formData.leaveType || ''}
                    onChange={handleChange}
                  >
                    {Object.values(LeaveType).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    name="reason"
                    label="Açıklama"
                    value={formData.reason || ''}
                    onChange={handleChange}
                  />
                </Stack>
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <TextField
                    select
                    fullWidth
                    name="status"
                    label="Durum"
                    value={formData.status || ''}
                    onChange={handleChange}
                  >
                    {Object.values(LeaveStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {status}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Typography variant="subtitle1">
                    <strong>Oluşturulma Tarihi:</strong> {new Date(permission.createdAt).toLocaleString('tr-TR')}
                  </Typography>
                  {permission.approvedAt && (
                    <Typography variant="subtitle1">
                      <strong>Onaylanma Tarihi:</strong> {new Date(permission.approvedAt).toLocaleString('tr-TR')}
                    </Typography>
                  )}
                  <Typography variant="subtitle1">
                    <strong>Onaylayan:</strong> {permission.approvedBy || '-'}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
            <Divider sx={{ my: 3 }} />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
              >
                {saving ? 'Kaydediliyor...' : 'Kaydet'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/dashboard/permissions')}
                disabled={saving}
              >
                İptal
              </Button>
            </Stack>
          </form>
        </Card>
      </Container>
  )
} 